/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import * as _ from 'lodash';
import template from './api-key-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {API_KEY_SCOPE_OPTIONS} from './api-key-commons';
import {ApiKeyApiService, ApiKeyModel} from './api-key-api.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {AppSharedModule} from '../../common/app-shared.module';
import {FormControlsModule} from '../../common/directives/form-controls/form-controls.module';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule, ButtonsModule, FormControlsModule],
  selector: 'adn-api-key-edit-page',
  template
})
export class ApiKeyEditPageComponent extends SignalAdminResourceEditPageBase<ApiKeyModel> {
  readonly scopeOptions = API_KEY_SCOPE_OPTIONS;
  readonly revoked = signal(false);
  readonly revokeError = signal<any>(null);

  constructor(
    private readonly apiKeyApi: ApiKeyApiService
  ) {
    super(apiKeyApi, 'apiKey');
  }

  hasScope(scope: string): boolean {
    return (this.editingModel.scopes || []).indexOf(scope) > -1;
  }

  scopesValid(): boolean {
    return !!this.editingModel.fullAccess || (this.editingModel.scopes || []).length > 0;
  }

  onScopesChanged(): void {
    this.markExternalFormDirty();
  }

  onFullAccessChanged(): void {
    this.markExternalFormDirty();
  }

  onExpiryChanged(): void {
    this.markExternalFormDirty();
  }

  revoke(): Promise<void> {
    this.revokeError.set(null);
    return firstValueFrom(this.apiKeyApi.revoke(this.editingModel.id)).then((data) => {
      this.revoked.set(true);
      const updated = {...this.state.model(), ...data, expired: true};
      this.editingModel.expired = true;
      if (data.expiry) {
        this.editingModel.expiry = data.expiry;
      }
      this.state.update(updated);
    }).catch((response) => {
      this.revokeError.set(response?.data || {message: 'Revoke failed.'});
    });
  }

  protected override initialiseModel(model: ApiKeyModel): ApiKeyModel {
    const initialised = super.initialiseModel(model);
    initialised.scopes = initialised.scopes || [];

    if (this.isNew) {
      initialised.fullAccess = false;
    }

    if (initialised.expiry && !initialised.modExpiry) {
      initialised.modExpiry = initialised.expiry;
    }
    return initialised;
  }

  protected override afterSave(model: ApiKeyModel): void {
    if (model.expiry) {
      model.modExpiry = model.expiry;
    }
  }

  protected override prepareForSubmit(model: ApiKeyModel): void {
    if (model.fullAccess) {
      model.scopes = ['NG_API'];
    } else {
      model.scopes = model.scopes || [];
      _.remove(model.scopes, (element: string) => element === 'NG_API');
    }

    model.expiry = _.isDate(model.modExpiry) ? model.modExpiry : null;
  }
}
